import {safeGet} from "../utils";

export function renderWebGlFingerprint(): string {
  const canvas = document.createElement('canvas');
  const gl = (canvas.getContext('webgl2') as WebGL2RenderingContext)
    || (canvas.getContext('webgl') as WebGLRenderingContext)
    || (canvas.getContext('experimental-webgl') as WebGLRenderingContext);
  if (!gl) return '';

  const parts: string[] = [];

  const debugInfo = safeGet(() => gl.getExtension('WEBGL_debug_renderer_info'));
  if (debugInfo) {
    parts.push('vendor=' + safeGet(() => gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)));
    parts.push('renderer=' + safeGet(() => gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)));
  } else {
    parts.push('vendor=null', 'renderer=null');
  }

  parts.push('glVersion=' + safeGet(() => gl.getParameter(gl.VERSION)));
  parts.push('shadingLanguageVersion=' + safeGet(() => gl.getParameter(gl.SHADING_LANGUAGE_VERSION)));
  parts.push('glVendor=' + safeGet(() => gl.getParameter(gl.VENDOR)));
  parts.push('glRenderer=' + safeGet(() => gl.getParameter(gl.RENDERER)));

  const numericParams: { name: string; param: number }[] = [
    {name: 'MAX_TEXTURE_SIZE', param: gl.MAX_TEXTURE_SIZE},
    {name: 'MAX_VIEWPORT_DIMS', param: gl.MAX_VIEWPORT_DIMS},
    {name: 'MAX_RENDERBUFFER_SIZE', param: gl.MAX_RENDERBUFFER_SIZE},
    {name: 'MAX_COMBINED_TEXTURE_IMAGE_UNITS', param: gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS},
    {name: 'MAX_CUBE_MAP_TEXTURE_SIZE', param: gl.MAX_CUBE_MAP_TEXTURE_SIZE},
    {name: 'MAX_FRAGMENT_UNIFORM_VECTORS', param: gl.MAX_FRAGMENT_UNIFORM_VECTORS},
    {name: 'MAX_TEXTURE_IMAGE_UNITS', param: gl.MAX_TEXTURE_IMAGE_UNITS},
    {name: 'MAX_VARYING_VECTORS', param: gl.MAX_VARYING_VECTORS},
    {name: 'MAX_VERTEX_ATTRIBS', param: gl.MAX_VERTEX_ATTRIBS},
    {name: 'MAX_VERTEX_TEXTURE_IMAGE_UNITS', param: gl.MAX_VERTEX_TEXTURE_IMAGE_UNITS},
    {name: 'MAX_VERTEX_UNIFORM_VECTORS', param: gl.MAX_VERTEX_UNIFORM_VECTORS},
    {name: 'ALIASED_LINE_WIDTH_RANGE', param: gl.ALIASED_LINE_WIDTH_RANGE},
    {name: 'ALIASED_POINT_SIZE_RANGE', param: gl.ALIASED_POINT_SIZE_RANGE},
  ];
  for (const {name, param} of numericParams) {
    const value = safeGet(() => gl.getParameter(param));
    const stringValue = value && (value as any).length !== undefined
      ? Array.from(value as ArrayLike<number>).join(',')
      : String(value);
    parts.push(`${name}=${stringValue}`);
  }

  const precisionTargets: { shader: number; precision: number; name: string }[] = [
    {shader: gl.VERTEX_SHADER, precision: gl.HIGH_FLOAT, name: 'VS_HIGH_FLOAT'},
    {shader: gl.VERTEX_SHADER, precision: gl.MEDIUM_FLOAT, name: 'VS_MEDIUM_FLOAT'},
    {shader: gl.VERTEX_SHADER, precision: gl.LOW_FLOAT, name: 'VS_LOW_FLOAT'},
    {shader: gl.FRAGMENT_SHADER, precision: gl.HIGH_FLOAT, name: 'FS_HIGH_FLOAT'},
    {shader: gl.FRAGMENT_SHADER, precision: gl.MEDIUM_FLOAT, name: 'FS_MEDIUM_FLOAT'},
    {shader: gl.FRAGMENT_SHADER, precision: gl.LOW_FLOAT, name: 'FS_LOW_FLOAT'},
  ];
  for (const {shader, precision, name} of precisionTargets) {
    const fmt = safeGet(() => gl.getShaderPrecisionFormat(shader, precision));
    parts.push(fmt ? `${name}=${fmt.rangeMin},${fmt.rangeMax},${fmt.precision}` : `${name}=null`);
  }

  const extensions = safeGet(() => gl.getSupportedExtensions());
  parts.push('extensions=' + (extensions ? [...extensions].sort().join(',') : 'null'));

  return parts.join('|');
}
